/**
 * Admin Location Maps API client — /api/admin-location-maps.php.
 * Credentialed (admin session). Backs the super-admin "Location Maps" designer.
 */

import type { AdminLocationMap, LocationMapFilter, LocationMapDisplay, LocationMapTemplates, MapDataset } from '../config/locationMaps';

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

export interface AdminLocationMapSummary { id: number; name: string; dataset: MapDataset; isActive: boolean }

export interface LocationMapPlacement {
  id: number;
  retailerId: number | null;
  placement: 'below_bom';
  mapId: number | null;
  isActive: boolean;
}

export interface LocationMapRetailerSummary { id: number; name: string; isPartner: boolean }

export interface LocationMapsListResponse {
  maps: AdminLocationMap[];
  placements: LocationMapPlacement[];
  retailers: LocationMapRetailerSummary[];
  upstreamConfigured: boolean;
}

export interface LocationMapPayload {
  name?: string;
  dataset?: MapDataset;
  filter?: LocationMapFilter;
  display?: LocationMapDisplay;
  templates?: LocationMapTemplates;
  strings?: Record<string, string>;
  isActive?: boolean;
}

export interface RetailerFacets {
  buyingGroups: { value: string; count: number }[];
  countries: { code: string; name: string; count: number }[];
  regions: { country: string; value: string; code: string | null; name: string; count: number }[];
  types: { typeId: number; type: string; count: number }[];
}
export interface InstallerFacets {
  countries: { code: string; name: string; count: number }[];
  regions: { country: string; code: string; name: string; count: number }[];
  industries: { residential: number; commercial: number; agriculture: number };
  published: Record<string, number>;
}
export type MapFacetsResponse =
  | { notConfigured: true }
  | { facets: RetailerFacets | InstallerFacets; cached: boolean; stale: boolean };

export class MapReferencedError extends Error {
  references: { actionBars: { id: number; retailerId: number | null; buttonId: string }[]; placements: { id: number; retailerId: number | null; placement: string }[] };
  constructor(refs: MapReferencedError['references']) {
    super('This map is still referenced by an action button or an inline placement.');
    this.references = refs;
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 409 && body?.error === 'referenced') throw new MapReferencedError(body.references);
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchLocationMapsFull(): Promise<LocationMapsListResponse> {
  const res = await fetch(`${API_BASE}/admin-location-maps.php`, { credentials: 'include' });
  return handle<LocationMapsListResponse>(res);
}

/** Lightweight list for pickers (the action-button designer). */
export async function fetchLocationMaps(): Promise<AdminLocationMapSummary[]> {
  const d = await fetchLocationMapsFull();
  return d.maps.map(m => ({ id: m.id, name: m.name, dataset: m.dataset, isActive: m.isActive }));
}

export async function fetchMapFacets(dataset: MapDataset): Promise<MapFacetsResponse> {
  const res = await fetch(`${API_BASE}/admin-location-maps.php?facets=1&dataset=${dataset}`, { credentials: 'include' });
  return handle<MapFacetsResponse>(res);
}

export async function createLocationMap(map: LocationMapPayload): Promise<{ map: AdminLocationMap }> {
  const res = await fetch(`${API_BASE}/admin-location-maps.php`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ map }),
  });
  return handle(res);
}

export async function updateLocationMap(id: number, map: LocationMapPayload): Promise<{ map: AdminLocationMap }> {
  const res = await fetch(`${API_BASE}/admin-location-maps.php?id=${id}`, {
    method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ map }),
  });
  return handle(res);
}

export async function deleteLocationMap(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/admin-location-maps.php?id=${id}`, { method: 'DELETE', credentials: 'include' });
  await handle(res);
}

/** mapId null = explicitly no inline map; 'inherit' removes a retailer row so it follows universal. */
export async function savePlacement(retailerId: number | null, mapId: number | null | 'inherit', placement: 'below_bom' = 'below_bom'): Promise<{ placement: LocationMapPlacement | null }> {
  const res = await fetch(`${API_BASE}/admin-location-maps.php?placements=1`, {
    method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ retailerId, placement, mapId }),
  });
  return handle(res);
}
